import { useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box, Button, Typography, Paper } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import Api from "lib/api/httpRequestHelper";

import Filters from "./components/filters";
import ModalConsultFilters from "./components/modal-consult-filters";
import SelectProductsTable from "./components/select-products-table";
import ModalComponent from "./components/modal";
import ProductsTable from "./components/products-table";

import UtilsFncs from "utils/UtilsFncs";

import {
  setSelectedProducts,
  updateProductFilters,
} from "redux/reducers/criarVitrineReducer";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import {
  mergeProductsByCodigo,
  SELECT_ALL_PRODUCTS_MAX,
} from "../utils/wizard-helpers";

export default function IncludeProducts() {
  const [isLoading, setIsLoading] = useState(false);
  const [modalFilters, setModalFilters] = useState({
    productSearch: "",
    productLine: null,
    commercialName: null,
    subcategory: null,
  });
  const [preSelectedProducts, setPreSelectedProducts] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const modalOpenRef = useRef(false);

  const [productsTablePagination, setProductsTablePagination] = useState({
    page: 1,
    sizePerPage: 10,
  });

  const dispatch = useDispatch();
  const snackbar = useSnackbar();

  const { selectedProducts, productFilters, formData } = useSelector(
    (state) => state.criarVitrine,
  );
  const productFiltersRef = useRef(productFilters);

  const buildSearchBody = useCallback(
    async ({
      mergedFilters,
      page,
      pageSize,
      filterValue,
      base64FileOverride,
    }) => {
      const eanValue = Array.isArray(mergedFilters?.sku)
        ? mergedFilters.sku.length > 0
          ? mergedFilters.sku.map((item) => item?.value).filter(Boolean)
          : null
        : mergedFilters?.sku?.value
          ? [mergedFilters.sku.value]
          : null;

      const base64File =
        base64FileOverride ??
        (mergedFilters?.importFile
          ? await UtilsFncs.FileToB64(mergedFilters?.importFile)
          : null);

      return {
        page,
        pageSize,
        idMarca: formData?.brand?.value,
        categoria: mergedFilters?.category?.label,
        subcategoria: mergedFilters?.subcategory?.label,
        colecao_produto: mergedFilters?.collection?.label,
        nomeComercial: mergedFilters?.commercialName?.label,
        linhaProduto: mergedFilters?.productLine?.label,
        status: mergedFilters?.status?.label,
        qualidadeProduto:
          mergedFilters?.quality?.value ??
          mergedFilters?.quality?.id ??
          mergedFilters?.quality?.label,
        apenasComEstoque: true,
        ean: eanValue?.toString(),
        filter: filterValue,
        base64File,
        fileType: mergedFilters?.importFile?.type,
      };
    },
    [formData?.brand?.value],
  );

  const handleSearch = useCallback(
    async (overrides) => {
      const { initializePreSelection, ...restOverrides } = overrides ?? {};
      const baseFilters = productFilters;
      const mergedFilters = { ...baseFilters, ...restOverrides };
      const page =
        restOverrides?.page ??
        mergedFilters?.page ??
        Math.max(0, (currentPage ?? 1) - 1);
      const sizePerPage =
        restOverrides?.sizePerPage ??
        mergedFilters?.sizePerPage ??
        currentSizePerPage;
      const filterValue =
        restOverrides?.filter ??
        restOverrides?.productSearch ??
        mergedFilters?.productSearch ??
        null;

      try {
        setIsLoading(true);
        dispatch(
          updateProductFilters({
            ...mergedFilters,
            page,
            sizePerPage,
          }),
        );

        const response = await Api.post(
          "product-showcase-list",
          "product",
          await buildSearchBody({
            mergedFilters,
            page,
            pageSize: sizePerPage,
            filterValue,
          }),
          true,
        );

        const lista = response.Result?.ListaProdutos ?? [];
        setProductsData(lista);
        setTotalSize(response.Result?.TotalRegistros ?? 0);
        if (initializePreSelection) {
          setPreSelectedProducts(selectedProducts);
          setModalFilters({
            productSearch: "",
            commercialName: mergedFilters.commercialName ?? null,
            subcategory: mergedFilters.subcategory ?? null,
            productLine: mergedFilters.productLine ?? null,
          });
        }

        if (lista.length > 0) {
          setOpen(true);
        } else {
          const msg = modalOpenRef.current
            ? "Nenhum produto encontrado para esta pesquisa. Ajuste o texto ou os filtros."
            : "Nenhum produto encontrado com os filtros selecionados.";
          snackbar.showMessage(msg, {
            severity: modalOpenRef.current ? "warning" : "error",
          });
          if (modalOpenRef.current) {
            setOpen(true);
          } else {
            setOpen(false);
          }
        }
      } catch (error) {
        snackbar.showMessage(
          "Não foi possível buscar os produtos. Tente novamente mais tarde.",
          { severity: "error" },
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      buildSearchBody,
      currentPage,
      currentSizePerPage,
      dispatch,
      productFilters,
      snackbar,
    ],
  );

  const handleSelectAllResults = useCallback(async () => {
    const mergedFilters = {
      ...productFiltersRef.current,
      ...modalFilters,
    };
    const pageSize = 100;
    const filterValue = modalFilters.productSearch || null;
    const base64File = mergedFilters?.importFile
      ? await UtilsFncs.FileToB64(mergedFilters?.importFile)
      : null;

    const all = [];
    let total = 0;
    let page = 0;
    const maxItems = SELECT_ALL_PRODUCTS_MAX;

    while (all.length < maxItems) {
      const response = await Api.post(
        "product-showcase-list",
        "product",
        await buildSearchBody({
          mergedFilters,
          page,
          pageSize,
          filterValue,
          base64FileOverride: base64File,
        }),
        true,
      );

      const list = response.Result?.ListaProdutos ?? [];
      total = response.Result?.TotalRegistros ?? total;
      all.push(...list);

      if (all.length >= total || list.length === 0) break;
      if (all.length >= maxItems) break;
      page += 1;
      if (page > 100) break;
    }

    const merged = mergeProductsByCodigo([], all);

    if (total > maxItems) {
      snackbar.showMessage(
        `Seleção limitada a ${maxItems} produtos (${total} encontrados). Refine os filtros para incluir outros.`,
        { severity: "warning" },
      );
    }

    return merged.slice(0, maxItems);
  }, [buildSearchBody, modalFilters, snackbar]);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  const handleModalSearch = useCallback(() => {
    setCurrentPage(1);
    handleSearch({
      ...productFiltersRef.current,
      ...modalFilters,
      filter: modalFilters.productSearch || null,
      productSearch: modalFilters.productSearch || null,
      page: 0,
      initializePreSelection: false,
    });
  }, [handleSearch, modalFilters]);

  const handleAddProducts = () => {
    dispatch(
      setSelectedProducts(
        mergeProductsByCodigo(selectedProducts, preSelectedProducts),
      ),
    );
    closeModal();
  };

  const handlePaginationChange = (page, sizePerPage) => {
    const nextSize = sizePerPage ?? currentSizePerPage;
    const nextPage = page ?? currentPage;

    setCurrentSizePerPage(nextSize);
    setCurrentPage(nextPage);

    handleSearch({
      ...productFilters,
      page: nextPage - 1,
      sizePerPage: nextSize,
    });
  };

  useEffect(() => {
    productFiltersRef.current = productFilters;
  }, [productFilters]);

  useEffect(() => {
    modalOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setModalFilters({
      productSearch: "",
      commercialName: productFilters.commercialName ?? null,
      subcategory: productFilters.subcategory ?? null,
      productLine: productFilters.productLine ?? null,
    });
    // Sincroniza filtros apenas ao abrir o modal (evita loop com productFilters).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 3,
          borderColor: themeColorsDesingSystem.stone.main,
        }}
      >
        <Filters
          handleSearch={handleSearch}
          searching={isLoading}
          disableImportFile={selectedProducts.length > 0}
          idMarca={formData?.brand?.value}
        />
      </Paper>
      <ModalComponent
        open={open}
        onClose={closeModal}
        width="92vw"
        title="Consultar Produtos"
      >
        <ModalConsultFilters
          values={modalFilters}
          onChange={setModalFilters}
          onSearch={handleModalSearch}
          searching={isLoading}
          idMarca={formData?.brand?.value}
          category={productFilters?.category}
        />
        <Box mt={2}>
          <SelectProductsTable
            data={productsData}
            loading={isLoading}
            totalSize={totalSize}
            currentPage={currentPage}
            currentSizePerPage={currentSizePerPage}
            selectedProducts={preSelectedProducts}
            setSelectedProducts={setPreSelectedProducts}
            handlePaginationChange={handlePaginationChange}
            onSelectAllResults={handleSelectAllResults}
          />
          <Box
            display="flex"
            justifyContent="flex-end"
            mt={2}
            pt={2}
            borderTop={`1px solid ${themeColorsDesingSystem.stone.main}`}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={preSelectedProducts.length === 0}
              onClick={handleAddProducts}
              sx={{ minWidth: 160, fontWeight: 700 }}
            >
              Adicionar
            </Button>
          </Box>
        </Box>
      </ModalComponent>
      <Paper
        variant="outlined"
        sx={{
          mt: 4,
          p: selectedProducts.length > 0 ? 2 : 6,
          borderColor: themeColorsDesingSystem.stone.main,
          minHeight: 200,
        }}
      >
        {selectedProducts.length === 0 && !isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <Inventory2OutlinedIcon
              sx={{
                fontSize: 48,
                color: themeColorsDesingSystem.stone.dark,
                mb: 2,
              }}
            />
            <Typography variant="h6" color="textSecondary">
              Nenhum produto adicionado
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              Utilize os filtros acima ou importe uma lista de SKUs para popular
              a vitrine.
            </Typography>
          </Box>
        ) : (
          <ProductsTable
            totalSize={selectedProducts?.length ?? 0}
            currentPage={productsTablePagination.page}
            currentSizePerPage={productsTablePagination.sizePerPage}
            setProductsTablePagination={setProductsTablePagination}
          />
        )}
      </Paper>
    </>
  );
}
